/**
 * RichEditor — `component('richEditor', [...])` PHP 컴포넌트의 TS 짝.
 *
 * 페이지 로드 시 [data-rich-editor] 요소를 모두 스캔해 QuillEditor 인스턴스를 마운트.
 * hidden input 과 `text-change` 이벤트로 양방향 sync. 이미지·동영상 업로드 URL,
 * 테이블 토글, 동영상 토글은 data-attribute 로 PHP 에서 주입한다.
 *
 * 절대규칙 #2 — Quill 은 본 모듈과 QuillEditor.ts 만 사용. 다른 곳에서 new Quill 금지.
 */
import { QuillEditor } from "./QuillEditor";
import { Logger } from "../utils/Logger";

const MOUNT_FLAG = "__richEditorMounted";

interface MountedEl extends HTMLElement {
    [MOUNT_FLAG]?: boolean;
}

function mountOne(host: HTMLElement): void {
    const el = host as MountedEl;
    if (el[MOUNT_FLAG]) return;
    el[MOUNT_FLAG] = true;

    const inputId = host.dataset.richEditorInput || "";
    const input = inputId ? document.getElementById(inputId) as HTMLInputElement | null : null;
    const uploadUrl = host.dataset.richEditorUploadUrl || "";
    const csrf = host.dataset.richEditorCsrf || "";
    const enableTable = host.dataset.richEditorEnableTable === "1";
    const enableVideo = host.dataset.richEditorEnableVideo === "1";
    const initialHtml = input?.value || host.dataset.richEditorInitial || "";

    const mountEl = host.querySelector<HTMLElement>(".admin-rich-editor__mount") || host;

    const editor = new QuillEditor(mountEl, {
        placeholder: host.dataset.richEditorPlaceholder || "내용을 입력하세요…",
        upload: uploadUrl ? { uploadUrl, csrf } : undefined,
        videoUpload: uploadUrl && enableVideo ? { uploadUrl, csrf } : undefined,
        enableTable,
        enableVideo,
        initialHtml,
    });

    const instance = editor.init();
    if (!instance) {
        Logger.debug("[RichEditor] Quill 인스턴스 생성 실패", host);
        return;
    }

    // hidden input 동기화
    if (input) {
        editor.onChange(() => {
            input.value = editor.getHtml();
        });
    }
}

/**
 * scope 내(또는 전체)의 모든 [data-rich-editor] 를 마운트.
 * 멱등 — 이미 마운트된 요소는 재초기화하지 않는다.
 */
function initAll(scope: ParentNode | null = document): void {
    if (!scope) return;
    scope.querySelectorAll<HTMLElement>("[data-rich-editor]").forEach((el) => {
        // 숨겨진 콘텐츠 타입 패널(예: 콘텐츠 블록 폼) 안의 에디터는 display:none 상태라
        // 부팅 시 마운트하면 Quill 높이/툴바가 0으로 깨진다. 패널이 보일 때 lazy-mount 한다.
        if (el.closest("[data-content-type-panel][hidden]")) return;
        mountOne(el);
    });
}

export const RichEditor = {
    initAll,
    mountOne,
};
